<template>
  <div class="page-content">
    <OrganismsLoading v-show="isLoading" />
    <div v-if="isLoading" style="height: 10000px"></div>
    <PagePartsSet
      v-else
      :page-parts="pagePartsRef"
      :child-parts-ids="childPartsIds"
      :delivery-lt-prefectures="deliveryLtPrefectures"
    />
  </div>
</template>

<script setup lang="ts">
import { PagePartsFragmentFragment } from "~/types/pageParts"
import { useStore } from "~/stores"
import {
  usePublicLogilessOutboundDeliveryCalcDeliveryLtPrefecturesQuery,
  usePublicProductFindOneQuery,
} from "~/gql/urql.generated"
import {
  Product,
  ProductFragmentFragment,
  PublicLogilessOutboundDeliveryCalcDeliveryLtPrefecturesQuery,
} from "~/types/type.generated"
import { ScriptPropertySrc, ScriptPropertyText } from "~/types/meta"
import {
  useCustomMeta,
  createProductJsonLD,
  createLdJson,
  createBreadcrumbJsonLDFromPageParts,
} from "~/composables/meta"
import { ApiPageOneAbsoluteUrlResponsePage } from "~/types/server/api/page"
import type { PagePartsSetType } from "~/types/page"
import { useSocial } from "~/composables/useSocial"
import { trackingTtqViewContent } from "~/utils/functions/tiktok"
import { trackLiglaAutoLineEvent } from "~/utils/functions/ligla"
// define layout
definePageMeta({ layout: "default-with-extended-footer" })

// composables
const store = useStore()
const route = useRoute()
const customMeta = useCustomMeta()
const config = useRuntimeConfig()
// ref and reactive object
const isLoading = useState<boolean>(() => true)
const pageRef = ref<ApiPageOneAbsoluteUrlResponsePage>()
const pagePartsRef = ref<PagePartsSetType[]>([])
const childPartsIds = ref<string[]>([])
const productRef = ref<ProductFragmentFragment>()

const deliveryLtPrefectures = ref<
  PublicLogilessOutboundDeliveryCalcDeliveryLtPrefecturesQuery["publicLogilessOutboundDeliveryCalcDeliveryLtPrefectures"]
>({
  default_prefecture: "",
  prefectures: [],
})

const { fetchWithHandleRedirect } = usePageOneApi()
const absoluteUrl = `products/${route.params.productSlug}`
const pageOneResponse = await fetchWithHandleRedirect(absoluteUrl, route)
pageRef.value = pageOneResponse.page
// TODO: 型修正
pagePartsRef.value = pageOneResponse.page_parts as PagePartsFragmentFragment[]
const isProduction = config.public.APP_ENV === "production"

const { updatePagePartsWithProductOnly, setProducts, createChildPagePartsList } = useFetchProductForPageParts()

// この商品ページの商品だけを取得して早めに商品ページのローディングを終わらせる
const loadProduct = async () => {
  const [{ data: product }, { data: calcDeliveryLtPrefecturesData }] = await Promise.all([
    usePublicProductFindOneQuery({
      requestPolicy: "network-only",
      variables: {
        filter: {
          document_id: pageRef.value?.target_product_id || "",
          is_status: true,
        },
      },
    }),
    usePublicLogilessOutboundDeliveryCalcDeliveryLtPrefecturesQuery({
      requestPolicy: "cache-first",
    }),
  ])

  productRef.value = product?.value?.publicProductFindOne as Product

  deliveryLtPrefectures.value = calcDeliveryLtPrefecturesData.value
    ?.publicLogilessOutboundDeliveryCalcDeliveryLtPrefectures ?? {
    default_prefecture: "",
    prefectures: [],
  }

  if (productRef.value) {
    updatePagePartsWithProductOnly(pagePartsRef.value, [productRef.value] as Product[])
    store.addProductBrowsingHistory(productRef.value)
  }

  isLoading.value = false
}

const { marketingTagsEvent } = useSocial()

await useAsyncData("usePublicPagePartsManyQuery", async () => {
  await Promise.all([loadProduct(), setProducts(pagePartsRef.value, pageRef.value?.target_product_id)])
  const { tempPartsList, tempChildPartsIds } = createChildPagePartsList(pagePartsRef.value)
  pagePartsRef.value = tempPartsList
  childPartsIds.value = tempChildPartsIds

  if (config.public.APP_ENV !== "production") return
  marketingTagsEvent(pagePartsRef.value)
  trackingTtqViewContent(route.fullPath, productRef.value)
  // Ligla 商品詳細ページイベント送信
  if (productRef.value?.document_id) trackLiglaAutoLineEvent("detail", productRef.value.document_id, route.fullPath)
})

onMounted(() => {
  // Karte Blocks のイベントを強制的に発火させる
  // ref. https://support.karte.io/post/5N5wcqgwmK7OwspGCI6ytX
  if (isProduction) {
    document.dispatchEvent(new Event("karte-blocks-force-activate"))
  }
})

// meta script
const metaData = computed(() => {
  const title = pageRef.value?.title || ""
  const description = pageRef.value?.description || ""
  const image = pageRef.value?.ogp_url || ""
  return {
    title,
    description,
    image,
  }
})

const metaScript = computed(() => {
  const script: (ScriptPropertyText | ScriptPropertySrc)[] = []
  const felmatTag = { src: "https://js.crossees.com/csslp.js" }
  const linkATag = { src: "https://link-ag.net/dist/p/l/index.js" }
  script.push(felmatTag)
  script.push(linkATag)
  return script
})

useHead(
  customMeta.fetch({
    title: `${metaData.value.title}`,
    description: `${metaData.value.description}`,
    image: `${metaData.value.image}`,
    script: metaScript.value,
    robots: pageRef.value?.noindex ? "noindex" : undefined,
    jsonLDInfo: [
      createProductJsonLD(productRef.value, pageRef.value?.description),
      createBreadcrumbJsonLDFromPageParts(pagePartsRef.value),
    ],
  }),
)

// productRef.valueをwatchしてjsonLDInfoのみ更新する
watch(productRef, () => {
  if (!productRef.value) return
  const jsonLDScript = createLdJson([
    createProductJsonLD(productRef.value, pageRef.value?.description),
    createBreadcrumbJsonLDFromPageParts(pagePartsRef.value),
  ])

  if (jsonLDScript) {
    useHead({
      script: [jsonLDScript],
    })
  }
})
</script>
